// ─────────────────────────────────────────────────────────────────────────────
// CRM 2.0 — useRowSelection
//
// Small, generic multi-select helper for list rows/cards. Tracks a set of
// selected ids and exposes toggle / select-all / clear operations plus the
// header "select all" tri-state. Kept UI-agnostic so the Lead table, Deal table,
// and Task board can all reuse it (Req 8.1 multi-select).
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useMemo, useState } from 'react';

export interface RowSelection {
  /** Currently selected ids. */
  selectedIds: string[];
  /** Fast membership test for a row. */
  isSelected: (id: string) => boolean;
  /** Toggle a single row's selection. */
  toggle: (id: string) => void;
  /** Whether every id in `allIds` is currently selected (and there is at least one). */
  allSelected: boolean;
  /** True when some—but not all—rows are selected (header checkbox indeterminate). */
  someSelected: boolean;
  /** Select every id in `allIds`, or clear if all are already selected. */
  toggleAll: () => void;
  /** Clear the entire selection. */
  clear: () => void;
  /** Number of selected rows. */
  count: number;
}

/**
 * @param allIds the ids of the rows currently visible in the list. Selection is
 *   intersected with this set so stale ids (filtered/paged away) never linger in
 *   `selectedIds`, keeping select-all and the submitted payload accurate.
 */
export function useRowSelection(allIds: string[]): RowSelection {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Intersect the raw selection with the currently visible ids. This is derived
  // (not stored) so changing filters/pages can't submit ids the user can't see.
  const visibleSelectedIds = useMemo(() => {
    const visible = new Set(allIds);
    return [...selected].filter((id) => visible.has(id));
  }, [selected, allIds]);

  const isSelected = useCallback((id: string) => selected.has(id), [selected]);

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allSelected = allIds.length > 0 && visibleSelectedIds.length === allIds.length;
  const someSelected = visibleSelectedIds.length > 0 && !allSelected;

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      const visible = new Set(allIds);
      const currentVisibleSelected = [...prev].filter((id) => visible.has(id));
      // If everything visible is already selected, clear the visible selection;
      // otherwise select all visible ids.
      if (allIds.length > 0 && currentVisibleSelected.length === allIds.length) {
        const next = new Set(prev);
        allIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      allIds.forEach((id) => next.add(id));
      return next;
    });
  }, [allIds]);

  const clear = useCallback(() => setSelected(new Set()), []);

  return {
    selectedIds: visibleSelectedIds,
    isSelected,
    toggle,
    allSelected,
    someSelected,
    toggleAll,
    clear,
    count: visibleSelectedIds.length,
  };
}
